import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import { PrelandCard } from '../../PrelandCard';
import { Indicator } from '../Indicator';
import { Button } from '../../Button';
import styles from './PrelandCardSelector.css';

const cx = classnames.bind(styles);

const SWIPE_LEFT = 'left';
const SWIPE_RIGHT = 'right';

const MAX_NUMBER = 8;

function PrelandCardSelector({cards, onSelectCard, onCardLeftScreen, isDisabled, withTeamRoleScore}) {
    const [hasSwipe, setHasSwipe] = useState(true);
    const childRefs = useMemo(
        () => Array(cards.length).fill(0).map((i) => React.createRef()),
        [cards]
    );

    const handleCardLeftScreen = useCallback((dir) => {
        if (onCardLeftScreen) {
            return onCardLeftScreen();
        }

        onSelectCard({card: cards[0], isSelected: dir === SWIPE_RIGHT});
    }, [cards]);

    const swipe = dir => async () => {
        setHasSwipe(false);

        await childRefs[0].current.swipe(dir);

        setHasSwipe(true);
    };

    const onSwipeRequirementFulfilled = () => {
        childRefs[1].current.setStyleByChild('transform', 'rotate(0deg)');
    };

    const {name, captainRoleScore, shooterRoleScore, defenderRoleScore} = cards[0] || {};

    return (
        <>
            <div className={styles.wrapper}>
                <Button
                    iconLeft={{type: 'close'}}
                    className={cx('buttonClose', {disabled: !hasSwipe || isDisabled})}
                    onClick={swipe(SWIPE_LEFT)}
                    type='tertiary'
                />
                <div className={styles.cards}>
                    {cards.map((card, index) => (
                        <PrelandCard
                            key={card.id}
                            className={styles.card}
                            onCardLeftScreen={handleCardLeftScreen}
                            ref={childRefs[index]}
                            swipeRequirementType='position'
                            style={{
                                zIndex: cards.length - index,
                                pointerEvents: index === 0 && !isDisabled ? 'auto' : 'none'
                            }}
                            onSwipeRequirementFulfilled={onSwipeRequirementFulfilled}
                            preventSwipe={['up', 'down']}
                        >
                            <div
                                style={{
                                    backgroundImage: `url(${card.imageUrl})`,
                                    transform: `rotate(${2.5 * (index > 6 ? 6 : index)}deg)`,
                                    boxShadow: index > 6 ? '' : 'rgba(0, 0, 0, 0.3) 0 1px 4px'
                                }}
                                className={styles.backgroundImage}
                            >
                                {withTeamRoleScore && (
                                    <p className={cx('teamRoleScore', {low: card.teamRoleScore < 6, medium: card.teamRoleScore === 6, high: card.teamRoleScore >= 7})}>
                                        <span>+</span>
                                        <span>{card.teamRoleScore}</span>
                                        <span className={styles.maxNumberText}>max {MAX_NUMBER}</span>
                                    </p>
                                )}
                            </div>
                        </PrelandCard>
                    ))}
                </div>
                <Button
                    iconLeft={{type: 'checked'}}
                    className={cx('buttonCheck', {disabled: !hasSwipe || isDisabled})}
                    onClick={swipe(SWIPE_RIGHT)}
                    type='tertiary'
                />
            </div>
            {Boolean(name) && (
                <div className={styles.cardInformation}>
                    <div className={styles.cardInformationItem}>
                        <span>Leadership</span>
                        <span>{captainRoleScore}/10</span>
                    </div>
                    <Indicator
                        className={styles.cardInformationItemIndicator}
                        value={captainRoleScore / 10 * 100}
                    />
                    <div className={styles.cardInformationItem}>
                        <span>Accuracy</span>
                        <span>{shooterRoleScore}/10</span>
                    </div>
                    <Indicator
                        className={styles.cardInformationItemIndicator}
                        value={shooterRoleScore / 10 * 100}
                    />
                    <div className={styles.cardInformationItem}>
                        <span>Survivability</span>
                        <span>{defenderRoleScore}/10</span>
                    </div>
                    <Indicator
                        className={styles.cardInformationItemIndicator}
                        value={defenderRoleScore / 10 * 100}
                    />
                </div>
            )}
        </>
    )
}

PrelandCardSelector.propTypes = {
    cards: PropTypes.array.isRequired,
    onSelectCard: PropTypes.func,
    onCardLeftScreen: PropTypes.func,
    isDisabled: PropTypes.bool,
    withTeamRoleScore: PropTypes.bool
};

PrelandCardSelector.defaultProps = {
    onSelectCard: () => null,
    onCardLeftScreen: null,
    isDisabled: false,
    withTeamRoleScore: true
};

export {
    PrelandCardSelector
}
